import { extractErrorMessage } from '../api/client';
import { CANCELADO, REINTENTOS_POR_LIMITE, SIGUE_EL_LIMITE, conEsperaPorLimite, incierto } from '../api/esperaPorLimite';

/**
 * Lo que comparten "Packs sin sincronizar" y "Combos sin sincronizar" (2026-11-02): sincronizar lo elegido de a lotes, con
 * el reintento ante el límite de pedidos, el resultado por SKU y el resumen; y los textos chicos de las dos pantallas.
 */

/** Tope por pedido (MAX_BASES_LOTE_PACKS y MAX_COMBOS_LOTE del backend). */
export const POR_LOTE = 100;

export type Resultado = { estado: 'ok' | 'error' | 'revisar'; mensaje: string };
export type Progreso = { actual: number; total: number; esperando?: boolean };
type RespuestaLote = { resultados: Array<{ sku: string; estado: Resultado['estado']; mensaje: string | null }> };

export const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('es-AR'));
export const plural = (k: number, uno: string, varios: string) => `${n(k)} ${k === 1 ? uno : varios}`;
export const nombrePublicacion = (p: { mlaId: string; variantId?: string | null }) => `${p.mlaId}${p.variantId ? ` / variante ${p.variantId}` : ''}`;
export const COLOR_RESULTADO: Record<Resultado['estado'], string> = { ok: 'text-green-700', error: 'text-red-700', revisar: 'text-amber-700' };

/**
 * Manda `items` de a lotes de POR_LOTE con `enviar`; si un lote falla entero, sigue con el resto. Avisa el progreso y los
 * resultados acumulados después de cada lote. Devuelve el resumen ("N empezaron a sincronizar · …"), o null si la pantalla
 * se cerró (`seguir()` dio false) y no hay que tocar más el estado.
 */
export async function sincronizarEnLotes<T extends { sku: string }>(
  items: T[],
  enviar: (lote: T[]) => Promise<RespuestaLote>,
  { seguir, progreso, resultados }: { seguir: () => boolean; progreso: (p: Progreso) => void; resultados: (r: Record<string, Resultado>) => void },
): Promise<string | null> {
  const lotes: T[][] = [];
  for (let i = 0; i < items.length; i += POR_LOTE) lotes.push(items.slice(i, i + POR_LOTE));
  const cuenta = { ok: 0, error: 0, revisar: 0 };
  const nuevos: Record<string, Resultado> = {};
  for (const [i, lote] of lotes.entries()) {
    if (!seguir()) return null;
    progreso({ actual: i + 1, total: lotes.length });
    try {
      const r = await conEsperaPorLimite(() => enviar(lote), (esperando) => progreso({ actual: i + 1, total: lotes.length, esperando }), seguir);
      for (const x of r.resultados) {
        nuevos[x.sku] = x.estado === 'ok'
          ? { estado: 'ok', mensaje: x.mensaje ?? 'Empezó a sincronizar: Mercado Libre se actualiza en unos minutos' }
          : { estado: x.estado, mensaje: `${x.estado === 'revisar' ? 'Revisar' : 'Error'}: ${x.mensaje ?? ''}` };
        cuenta[x.estado]++;
      }
    } catch (e) {
      if (e === CANCELADO) return null;
      const mensaje = e === SIGUE_EL_LIMITE
        ? `Error: el servidor siguió pidiendo esperar (límite de pedidos) después de ${REINTENTOS_POR_LIMITE} reintentos; no se sincronizó, volvé a intentarlo más tarde`
        : `Error: ${extractErrorMessage(e)}${incierto(e) ? ' (puede haberse aplicado igual: revisá el estado)' : ''}`;
      for (const x of lote) { nuevos[x.sku] = { estado: 'error', mensaje }; cuenta.error++; }
    }
    resultados({ ...nuevos });
  }
  if (!seguir()) return null;
  return [
    cuenta.ok === 1 ? '1 empezó a sincronizar' : `${n(cuenta.ok)} empezaron a sincronizar`,
    ...(cuenta.error > 0 ? [`${n(cuenta.error)} con error`] : []),
    ...(cuenta.revisar > 0 ? [`${n(cuenta.revisar)} para revisar`] : []),
  ].join(' · ');
}
